import type { ParsedQuery } from 'query-string'
import { useEffect, useRef, useState } from 'react'

import { FvButton, LoadMore, OrDivider } from '@fv/client-components'

import { FooterBar } from '@/components/FooterBar'
import InfoBox from '@/components/InfoBox'
import { supportMessage } from '@/constants'

import { AuditFiltersProvider } from '../shipment-filters/AuditFiltersProvider'
import { CompactFilterBar } from '../shipment-filters/CompactFilterBar'
import { ShipmentFilterPanel } from '../shipment-filters/FilterPanel'
import { useHandleFilterChange } from '../shipment-filters/hooks'
import AuditListCard from './AuditListCard'
import { AuditListHeader } from './AuditListHeader'
import { useRemitPayments } from './mutations'
import { defaultAuditStatus, useAuditList, useAuditListStatus } from './queries'
import { colorSuccess } from './types'

export const AuditPage = () => {
  const auditsQuery = useAuditList()
  const auditStatus = useAuditListStatus() ?? defaultAuditStatus
  const { remitPayments, isArchiving, isExporting } = useRemitPayments()

  const [activeAudit, setActiveAudit] = useState<string | null>(null)
  const [auditsToArchive, setAuditsToArchive] = useState<string[]>([])
  const archiveCount = auditsToArchive.length
  const isAccepted = auditStatus === 'accepted'
  const isBusy = isArchiving || isExporting
  const listRef = useRef<HTMLDivElement | null>(null)
  const { handle: handleFilterChange } = useHandleFilterChange(listRef)
  // biome-ignore lint/correctness/useExhaustiveDependencies: UX
  useEffect(() => {
    setActiveAudit(null)
  }, [auditStatus])

  useEffect(() => {
    if (isAccepted) {
      setAuditsToArchive(auditsQuery.data.map(a => a.loadId))
    }
  }, [auditsQuery.data, isAccepted])

  const handleChange = (data: ParsedQuery) => {
    handleFilterChange({
      ...data,
      status: [auditStatus],
    })
  }

  return (
    <AuditFiltersProvider>
      <div className="shipper-page-fill shipper-grid-rows-single-fill grid bg-fv-gray-100 grid-auditing grid-cols-[19rem_1fr] b1300:grid-cols-1 b1300:grid-rows-[auto_minmax(0,1fr)]">
        <ShipmentFilterPanel drawerAt="b1300" onChange={handleChange} />
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <div
            className="audit-cards-ctn min-h-0 flex-1 overflow-auto"
            ref={listRef}
          >
            <AuditListHeader />
            <div className="b1300:block border-t-fv-gray -mt-2 mb-6 hidden basis-full border-t border-dashed pt-4">
              <CompactFilterBar onChange={handleChange} />
            </div>
            {auditsQuery.isError && (
              <InfoBox icon="triangle">
                Unable to load audit list, {supportMessage}
              </InfoBox>
            )}

            {auditsQuery.isLoading ? (
              <InfoBox icon="spinner">Loading...</InfoBox>
            ) : (
              <>
                {auditsQuery.data.map(a => (
                  <AuditListCard
                    auditsToArchive={auditsToArchive}
                    data={a}
                    isActive={a.loadId === activeAudit}
                    isRemitting={isBusy && auditsToArchive.includes(a.loadId)}
                    key={a.loadId}
                    setActiveAudit={setActiveAudit}
                    setAuditsToArchive={setAuditsToArchive}
                    status={auditStatus}
                  />
                ))}

                {!auditsQuery.data.length && (
                  <InfoBox>No {auditStatus} invoices found.</InfoBox>
                )}
              </>
            )}

            <LoadMore
              className="mt-4"
              fetchNextPage={auditsQuery.fetchNextPage}
              hasNextPage={auditsQuery.hasNextPage}
              isLoading={auditsQuery.isFetchingNextPage}
            />
          </div>
        </div>
        {isAccepted && archiveCount > 0 && (
          <FooterBar jc className="col-span-full">
            <FvButton
              onClick={() => {
                !isBusy &&
                  remitPayments({
                    loadIds: auditsToArchive,
                  })
              }}
              icon="archive"
              loading={isArchiving}
              theme="default"
            >
              <span>Archive only - {archiveCount} checked items</span>
            </FvButton>
            <OrDivider className="mx-3" />
            <FvButton
              theme="primary"
              onClick={() => {
                !isBusy &&
                  remitPayments({
                    doExport: true,
                    loadIds: auditsToArchive,
                  })
              }}
              icon="archive"
              loading={isExporting}
              iconClass={colorSuccess}
            >
              <span>Archive & export - {archiveCount} checked items</span>
            </FvButton>
          </FooterBar>
        )}
      </div>
    </AuditFiltersProvider>
  )
}
